import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import {
  ArrowLeft, Calendar, Tag, CheckCircle2, MapPin, MessageCircle,
  Lightbulb, Truck, Package, KeyRound, Footprints, Ban, Info,
  Egg, Bird, Ham, Drumstick,
} from "lucide-react";
import { productsAPI, ordersAPI } from "../../api";
import { useAuth } from "../../context/AuthContext";
import PaymentModal from "../PaymentModal";
import ReviewsList from "../Reviews/ReviewsList";
import LocationShare from "../Location/LocationShare";
import Hint from "../Onboarding/Hint";
import "./ProductDetail.css";

// Fonction pour formater l'unité avec le pluriel correct
const formatUnit = (unit, quantity) => {
  if (quantity > 1) {
    if (unit === "poulet") return "poulets";
    if (unit === "pièce") return "pièces";
    if (unit === "lot") return "lots";
  }
  return unit;
};

const PRODUCT_TYPE_ICONS = {
  eggs: Egg,
  poultry: Bird,
  other: Ham,
  chicken: Drumstick,
};

const ProductDetail = () => {
  const { productId } = useParams();
  const { isAuthenticated, isRestaurant, isBreeder, user } = useAuth();
  const navigate = useNavigate();
  
  const [product, setProduct] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedImageIndex, setSelectedImageIndex] = useState(0);
  const [orderForm, setOrderForm] = useState({
    quantity: 1,
    delivery_address: "",
    delivery_type: "",
    meeting_point_address: "",
    notes: "",
  });
  const [deliveryPrice, setDeliveryPrice] = useState(null);
  const [loadingDeliveryPrice, setLoadingDeliveryPrice] = useState(false);
  const [deliveryPriceError, setDeliveryPriceError] = useState("");
  const [ordering, setOrdering] = useState(false);
  const [orderSuccess, setOrderSuccess] = useState(false);
  const [createdOrder, setCreatedOrder] = useState(null);
  const [showPaymentModal, setShowPaymentModal] = useState(false);

  const loadProduct = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const response = await productsAPI.getOne(productId);
      setProduct(response.data);
    } catch (err) {
      setError("Erreur lors du chargement du produit");
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [productId]);

  useEffect(() => {
    loadProduct();
  }, [loadProduct]);

  // Calculer le prix de livraison quand le type de livraison change
  useEffect(() => {
    const calculateDeliveryPrice = async () => {
      if (!product || !orderForm.delivery_type ||
          (orderForm.delivery_type !== "breeder" && orderForm.delivery_type !== "third_party")) {
        setDeliveryPrice(null);
        setDeliveryPriceError("");
        return;
      }

      setLoadingDeliveryPrice(true);
      setDeliveryPriceError("");
      try {
        const response = await ordersAPI.estimateDeliveryPrice({
          delivery_type: orderForm.delivery_type,
          product_id: parseInt(productId),
          delivery_address: orderForm.delivery_address
        });
        setDeliveryPrice(response.data.price);
      } catch (err) {
        console.error("Erreur lors du calcul du prix de livraison:", err);
        setDeliveryPrice(null);
        setDeliveryPriceError(err.response?.data?.detail || "Impossible de calculer le prix de livraison. Vérifiez vos adresses.");
      } finally {
        setLoadingDeliveryPrice(false);
      }
    };

    calculateDeliveryPrice();
  }, [product, productId, orderForm.delivery_type, orderForm.delivery_address]);

  const handleOrderSubmit = async (e) => {
    e.preventDefault();
    if (!isAuthenticated || !isRestaurant) {
      navigate("/login");
      return;
    }

    setOrdering(true);
    setError("");
    try {
      const orderData = {
        product_id: parseInt(productId),
        quantity: orderForm.quantity,
        delivery_address: orderForm.delivery_address,
        delivery_type: orderForm.delivery_type,
        // delivery_price est maintenant calculé automatiquement par le backend
        meeting_point_address: orderForm.meeting_point_address,
        notes: orderForm.notes,
      };
      
      const response = await ordersAPI.create(orderData);
      
      // Stocker la commande créée et ouvrir le modal de paiement
      setCreatedOrder(response.data);
      setShowPaymentModal(true);
      setOrderSuccess(false); // Ne pas afficher le message de succès, on ouvre directement le paiement
    } catch (err) {
      setError(err.response?.data?.detail || "Erreur lors de la commande");
    } finally {
      setOrdering(false);
    }
  };

  if (loading) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  if (error && !product) {
    return (
      <div className="container main-content">
        <div className="alert alert-error">{error}</div>
        <Link to="/products" className="btn btn-primary">
          Retour aux produits
        </Link>
      </div>
    );
  }

  if (!product) return null;

  return (
    <div className="container main-content">
      <Link to="/products" className="back-link icon-inline">
        <ArrowLeft size={16} strokeWidth={2.25} /> Retour aux produits
      </Link>

      <div className="product-detail-container">
        <div className="product-detail-main">
          <div className="product-detail-image" data-tour="product-detail">
            {(() => {
              const images = product.images && product.images.length > 0 
                ? product.images.map(img => img.image_url)
                : product.image_url 
                  ? [product.image_url]
                  : [];
              
              if (images.length === 0) {
                const TypeIcon = PRODUCT_TYPE_ICONS[product.product_type] || Drumstick;
                return (
                  <div style={{
                    width: "100%",
                    height: "100%",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    background: "linear-gradient(135deg, var(--primary-light) 0%, var(--secondary) 100%)",
                  }}>
                    <TypeIcon size={96} strokeWidth={2} color="#fff" style={{ filter: "drop-shadow(0 3px 6px rgba(0, 0, 0, 0.3))" }} />
                  </div>
                );
              }
              
              return (
                <>
                  <img 
                    src={images[selectedImageIndex]} 
                    alt={product.name} 
                    onError={(e) => { e.target.style.display = 'none'; }} 
                  />
                  {images.length > 1 && (
                    <>
                      {selectedImageIndex > 0 && (
                        <button
                          onClick={() => setSelectedImageIndex(selectedImageIndex - 1)}
                          className="btn btn-primary"
                          style={{
                            position: "absolute",
                            left: "1rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10
                          }}
                        >
                          ‹
                        </button>
                      )}
                      {selectedImageIndex < images.length - 1 && (
                        <button
                          onClick={() => setSelectedImageIndex(selectedImageIndex + 1)}
                          className="btn btn-primary"
                          style={{
                            position: "absolute",
                            right: "1rem",
                            top: "50%",
                            transform: "translateY(-50%)",
                            zIndex: 10
                          }}
                        >
                          ›
                        </button>
                      )}
                      <div style={{
                        position: "absolute",
                        bottom: "1rem",
                        left: "50%",
                        transform: "translateX(-50%)",
                        display: "flex",
                        gap: "0.5rem",
                        zIndex: 10
                      }}>
                        {images.map((_, index) => (
                          <button
                            key={index}
                            onClick={() => setSelectedImageIndex(index)}
                            className="btn btn-sm"
                            style={{
                              width: "10px",
                              height: "10px",
                              padding: 0,
                              borderRadius: "50%",
                              background: index === selectedImageIndex ? "var(--primary)" : "rgba(255,255,255,0.5)",
                              border: "1px solid white"
                            }}
                          />
                        ))}
                      </div>
                    </>
                  )}
                  {images.length > 1 && (
                    <div style={{
                      position: "absolute",
                      top: "1rem",
                      right: "1rem",
                      background: "rgba(0,0,0,0.5)",
                      color: "white",
                      padding: "0.25rem 0.5rem",
                      borderRadius: "var(--radius-sm)",
                      fontSize: "0.875rem"
                    }}>
                      {selectedImageIndex + 1} / {images.length}
                    </div>
                  )}
                </>
              );
            })()}
          </div>
          
          {product.images && product.images.length > 1 && (
            <div style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fill, minmax(80px, 1fr))",
              gap: "0.5rem",
              marginTop: "1rem"
            }}>
              {product.images.map((img, index) => (
                <img
                  key={index}
                  src={img.image_url}
                  alt={`${product.name} ${index + 1}`}
                  onClick={() => setSelectedImageIndex(index)}
                  style={{
                    width: "100%",
                    height: "80px",
                    objectFit: "cover",
                    borderRadius: "var(--radius-sm)",
                    border: index === selectedImageIndex ? "2px solid var(--primary)" : "1px solid var(--border-light)",
                    cursor: "pointer",
                    opacity: index === selectedImageIndex ? 1 : 0.7
                  }}
                />
              ))}
            </div>
          )}
          
          <div className="product-detail-info">
            <div className="product-detail-header">
              <h1>{product.name}</h1>
              <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginTop: "0.5rem" }}>
                {product.product_type && (
                  <span className="badge badge-info" style={{ fontSize: "0.9rem" }}>
                    {product.product_type === "chicken" ? "🐔 Poulet" : 
                     product.product_type === "eggs" ? "🥚 Œufs" : 
                     product.product_type === "poultry" ? "🦆 Volaille" : 
                     "🐷 Autre"}
                  </span>
                )}
                {product.is_live && (
                  <span className="badge badge-success" style={{ fontSize: "0.9rem" }}>
                    🐔 Vivant
                  </span>
                )}
                {product.category && (
                  <span className="badge badge-primary">{product.category}</span>
                )}
                {product.available_from && new Date(product.available_from) > new Date() && (
                  <span className="badge badge-warning icon-inline" style={{ fontSize: "0.9rem" }}>
                    <Calendar size={14} strokeWidth={2.25} /> Disponible le {new Date(product.available_from).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </span>
                )}
              </div>
            </div>

            <div className="product-detail-price" style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
              <span style={{ fontSize: "2.5rem", fontWeight: "bold" }}>
                {product.unit === "lot" && product.lot_price 
                  ? product.lot_price.toLocaleString("fr-FR")
                  : product.price.toLocaleString("fr-FR")}
              </span>
              <span style={{ fontSize: "1.2rem", color: "var(--text-secondary)" }}>
                F CFA {product.unit === "lot" && product.lot_size ? `/ lot (${product.lot_size} ${product.lot_size > 1 ? "éléments" : "élément"})` : `/ ${product.unit}`}
              </span>
            </div>

            <div style={{ marginTop: "1rem", fontSize: "1.1rem", color: "var(--text-primary)" }}>
              <strong>Stock disponible :</strong> {product.stock} {formatUnit(product.unit, product.stock)}
            </div>

            <div className="product-features">
              {product.category && (
                <div className="product-feature">
                  <span className="product-feature-icon"><Tag size={22} strokeWidth={2} /></span>
                  <div>
                    <strong>Catégorie</strong>
                    <div>{product.category}</div>
                  </div>
                </div>
              )}
              {product.owner?.certification && (
                <div className="product-feature">
                  <span className="product-feature-icon"><CheckCircle2 size={22} strokeWidth={2} /></span>
                  <div>
                    <strong>Certification</strong>
                    <div>{product.owner.certification}</div>
                  </div>
                </div>
              )}
              {product.available_from && new Date(product.available_from) > new Date() && (
                <div className="product-feature">
                  <span className="product-feature-icon"><Calendar size={22} strokeWidth={2} /></span>
                  <div>
                    <strong>Disponible à partir du</strong>
                    <div>
                      {new Date(product.available_from).toLocaleDateString("fr-FR", {
                        day: "numeric",
                        month: "long",
                        year: "numeric",
                        hour: "2-digit",
                        minute: "2-digit"
                      })}
                    </div>
                  </div>
                </div>
              )}
            </div>

            <div className="product-detail-description">
              <h3>Description</h3>
              <p>{product.description || "Aucune description disponible"}</p>
            </div>

            <div className="product-detail-owner card">
              <h3>Éleveur</h3>
              <p><strong>{product.owner?.name}</strong></p>
              {product.owner?.city && (
                <p className="icon-inline"><MapPin size={14} strokeWidth={2.25} /> {product.owner.city}</p>
              )}
              {product.owner?.certification && (
                <p className="certification icon-inline"><CheckCircle2 size={14} strokeWidth={2.25} /> {product.owner.certification}</p>
              )}
              {product.owner?.description && (
                <p>{product.owner.description}</p>
              )}
              {/* Bouton "Contacter l'éleveur" - Uniquement pour les restaurants (jamais pour les éleveurs) */}
              {isAuthenticated && isRestaurant && !isBreeder && product.owner?.id && user?.id !== product.owner.id && (
                <Link
                  to={`/messages?user_id=${product.owner.id}`}
                  className="btn btn-outline"
                  style={{ marginTop: "1rem", width: "100%" }}
                >
                  <MessageCircle size={16} strokeWidth={2.25} /> Contacter l'éleveur
                </Link>
              )}
              {!isAuthenticated && product.owner?.id && (
                <Link
                  to={`/login?redirect=/products/${productId}`}
                  className="btn btn-outline"
                  style={{ marginTop: "1rem", width: "100%" }}
                >
                  <MessageCircle size={16} strokeWidth={2.25} /> Contacter l'éleveur (connexion requise)
                </Link>
              )}
            </div>

            {/* Avis de l'éleveur */}
            {product.owner?.id && (
              <div className="card" style={{ marginTop: "2rem" }}>
                <ReviewsList userId={product.owner.id} />
              </div>
            )}
          </div>
        </div>

        {isRestaurant && product.is_available && product.stock > 0 && (
          <div className="product-detail-order card" data-tour="product-order-form">
            <h2>{product.available_from && new Date(product.available_from) > new Date() ? "Réserver en avance" : "Passer une commande"}</h2>

            <Hint id="order_delivery_types">
              <strong>Livraison par l'éleveur</strong> ou <strong>par un tiers</strong> : livré chez vous avec suivi.{" "}
              <strong>Point de rencontre</strong> : vous récupérez à une adresse convenue. Dans tous les cas, un code OTP sécurise la remise.
            </Hint>

            {product.available_from && new Date(product.available_from) > new Date() && (
              <div className="alert alert-info" style={{ marginBottom: "1rem" }}>
                <strong className="icon-inline"><Calendar size={16} strokeWidth={2.25} /> Réservation en avance</strong>
                <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.9rem" }}>
                  Ce produit sera disponible le{" "}
                  <strong>
                    {new Date(product.available_from).toLocaleDateString("fr-FR", {
                      day: "numeric",
                      month: "long",
                      year: "numeric",
                      hour: "2-digit",
                      minute: "2-digit"
                    })}
                  </strong>
                  . Vous pouvez réserver dès maintenant pour garantir votre commande.
                </p>
              </div>
            )}
            
            {/* Bouton "Discuter avec l'éleveur" - Uniquement pour les restaurants (jamais pour les éleveurs) */}
            {product.owner?.id && isRestaurant && !isBreeder && user?.id !== product.owner.id && (
              <div style={{ marginBottom: "1rem", padding: "1rem", background: "var(--background)", borderRadius: "var(--radius-md)", border: "1px solid var(--border-light)" }}>
                <p className="icon-inline" style={{ margin: "0 0 0.5rem 0", fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                  <Lightbulb size={15} strokeWidth={2.25} /> Vous avez une question avant de commander ?
                </p>
                <Link
                  to={`/messages?user_id=${product.owner.id}`}
                  className="btn btn-outline btn-sm"
                  style={{ width: "100%" }}
                >
                  <MessageCircle size={15} strokeWidth={2.25} /> Discuter avec l'éleveur
                </Link>
              </div>
            )}
            
            {error && <div className="alert alert-error">{error}</div>}

            <form onSubmit={handleOrderSubmit}>
              <div className="form-group">
                <label className="form-label">Quantité ({product.unit})</label>
                <input
                  type="number"
                  className="form-control"
                  min="1"
                  max={product.stock}
                  value={orderForm.quantity}
                  onChange={(e) =>
                    setOrderForm({
                      ...orderForm,
                      quantity: parseInt(e.target.value) || 1,
                    })
                  }
                  required
                />
              </div>

              <div className="form-group">
                <label className="form-label">
                  Type de livraison *
                  <span className="info-tooltip" title="Sélectionnez le mode de livraison qui vous convient">
                    <Info size={14} strokeWidth={2.25} />
                  </span>
                </label>
                <select
                  className="form-control"
                  value={orderForm.delivery_type}
                  onChange={(e) =>
                    setOrderForm({
                      ...orderForm,
                      delivery_type: e.target.value,
                      meeting_point_address: e.target.value !== "meeting_point" ? "" : orderForm.meeting_point_address,
                    })
                  }
                  required
                >
                  <option value="">Sélectionnez un type de livraison</option>
                  <option value="breeder">Livraison par l'éleveur</option>
                  <option value="restaurant">Livraison par le restaurant</option>
                  <option value="third_party">Livraison par un tiers</option>
                  <option value="meeting_point">Point de rencontre</option>
                </select>
                
                {/* Bulles d'information pour chaque type de livraison */}
                {orderForm.delivery_type === "breeder" && (
                  <div className="delivery-info-bubble">
                    <div className="info-icon"><Truck size={22} strokeWidth={2} /></div>
                    <div className="info-content">
                      <strong>Livraison par l'éleveur</strong>
                      <p>L'éleveur se charge personnellement de livrer votre commande à l'adresse indiquée.</p>
                      <ul style={{ marginTop: "0.5rem", paddingLeft: "1.2rem", fontSize: "0.85rem" }}>
                        <li className="icon-inline"><Package size={13} strokeWidth={2.25} /> Numéro de suivi obligatoire</li>
                        <li className="icon-inline"><KeyRound size={13} strokeWidth={2.25} /> Code OTP généré à l'expédition</li>
                        <li className="icon-inline"><CheckCircle2 size={13} strokeWidth={2.25} /> Paiement libéré après confirmation de réception</li>
                      </ul>
                    </div>
                  </div>
                )}

                {orderForm.delivery_type === "restaurant" && (
                  <div className="delivery-info-bubble">
                    <div className="info-icon"><Footprints size={22} strokeWidth={2} /></div>
                    <div className="info-content">
                      <strong>Livraison par le restaurant</strong>
                      <p>Vous récupérez la commande directement chez l'éleveur. Vous organisez vous-même le transport et allez chercher la commande à l'adresse de l'éleveur.</p>
                      <ul style={{ marginTop: "0.5rem", paddingLeft: "1.2rem", fontSize: "0.85rem" }}>
                        <li className="icon-inline"><MapPin size={13} strokeWidth={2.25} /> Récupération directement chez l'éleveur</li>
                        <li className="icon-inline"><Ban size={13} strokeWidth={2.25} /> Aucune adresse de livraison nécessaire</li>
                        <li className="icon-inline"><Ban size={13} strokeWidth={2.25} /> Aucun numéro de suivi requis</li>
                        <li className="icon-inline"><KeyRound size={13} strokeWidth={2.25} /> Code OTP généré à l'expédition pour confirmer la réception</li>
                        <li className="icon-inline"><CheckCircle2 size={13} strokeWidth={2.25} /> Paiement libéré après confirmation de l'OTP</li>
                      </ul>
                    </div>
                  </div>
                )}

                {orderForm.delivery_type === "third_party" && (
                  <div className="delivery-info-bubble">
                    <div className="info-icon"><Package size={22} strokeWidth={2} /></div>
                    <div className="info-content">
                      <strong>Livraison par un tiers</strong>
                      <p>Un transporteur tiers (livreur, service de livraison) se charge de la livraison.</p>
                      <ul style={{ marginTop: "0.5rem", paddingLeft: "1.2rem", fontSize: "0.85rem" }}>
                        <li className="icon-inline"><Package size={13} strokeWidth={2.25} /> Numéro de suivi obligatoire</li>
                        <li className="icon-inline"><KeyRound size={13} strokeWidth={2.25} /> Code OTP généré à l'expédition</li>
                        <li className="icon-inline"><CheckCircle2 size={13} strokeWidth={2.25} /> Paiement libéré après confirmation de réception</li>
                      </ul>
                    </div>
                  </div>
                )}

                {orderForm.delivery_type === "meeting_point" && (
                  <div className="delivery-info-bubble">
                    <div className="info-icon"><MapPin size={22} strokeWidth={2} /></div>
                    <div className="info-content">
                      <strong>Point de rencontre</strong>
                      <p>Vous convenez d'un lieu de rendez-vous avec l'éleveur pour récupérer votre commande.</p>
                      <ul style={{ marginTop: "0.5rem", paddingLeft: "1.2rem", fontSize: "0.85rem" }}>
                        <li className="icon-inline"><MapPin size={13} strokeWidth={2.25} /> Adresse du point de rencontre requise</li>
                        <li className="icon-inline"><Ban size={13} strokeWidth={2.25} /> Aucun numéro de suivi requis</li>
                        <li className="icon-inline"><KeyRound size={13} strokeWidth={2.25} /> Code OTP généré à l'expédition et envoyé par email/SMS</li>
                        <li className="icon-inline"><CheckCircle2 size={13} strokeWidth={2.25} /> Paiement libéré après confirmation de l'OTP</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>

              {orderForm.delivery_type === "meeting_point" && (
                <LocationShare
                  value={orderForm.meeting_point_address}
                  onChange={(e) =>
                    setOrderForm({
                      ...orderForm,
                      meeting_point_address: e.target.value,
                    })
                  }
                  placeholder="Adresse complète du point de rencontre"
                  label="Adresse du point de rencontre"
                  required={true}
                />
              )}

              {(orderForm.delivery_type === "breeder" || orderForm.delivery_type === "third_party") && (
                <>
                  <div className="form-group">
                    <label className="form-label">Prix de livraison</label>
                    {loadingDeliveryPrice ? (
                      <div style={{ padding: "0.75rem", background: "var(--background)", borderRadius: "var(--radius-sm)" }}>
                        <span>Calcul en cours...</span>
                      </div>
                    ) : deliveryPrice !== null ? (
                      <div style={{ 
                        padding: "0.75rem", 
                        background: "rgba(33, 150, 243, 0.1)", 
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid #2196F3"
                      }}>
                        <strong style={{ fontSize: "1.1rem", color: "#1976D2" }}>
                          {deliveryPrice.toLocaleString("fr-FR")} F CFA
                        </strong>
                        <p style={{ margin: "0.5rem 0 0 0", fontSize: "0.85rem", color: "var(--text-secondary)" }}>
                          Prix calculé automatiquement par la plateforme selon votre localisation
                        </p>
                      </div>
                    ) : (
                      <div style={{ padding: "0.75rem", background: "rgba(255, 193, 7, 0.1)", borderRadius: "var(--radius-sm)" }}>
                        <span style={{ color: "#856404" }}>{deliveryPriceError || "Impossible de calculer le prix. Vérifiez vos adresses."}</span>
                      </div>
                    )}
                  </div>
                  <LocationShare
                    value={orderForm.delivery_address}
                    onChange={(e) =>
                      setOrderForm({
                        ...orderForm,
                        delivery_address: e.target.value,
                      })
                    }
                    placeholder="Adresse complète"
                    label="Adresse de livraison"
                    required={false}
                  />
                </>
              )}

              <div className="form-group">
                <label className="form-label">Notes (optionnel)</label>
                <textarea
                  className="form-control"
                  value={orderForm.notes}
                  onChange={(e) =>
                    setOrderForm({ ...orderForm, notes: e.target.value })
                  }
                  rows="3"
                  placeholder="Instructions spéciales..."
                />
              </div>

              <div className="order-total">
                {(() => {
                  const productPrice = product.unit === "lot" && product.lot_price
                    ? product.lot_price * orderForm.quantity
                    : product.price * orderForm.quantity;
                  const deliveryPriceValue = deliveryPrice || 0;
                  const total = productPrice + deliveryPriceValue;
                  return (
                    <div>
                      <div style={{ marginBottom: "0.5rem", fontSize: "0.9rem", color: "var(--text-secondary)" }}>
                        <div>Prix produits: {productPrice.toLocaleString("fr-FR")} F CFA</div>
                        {(orderForm.delivery_type === "breeder" || orderForm.delivery_type === "third_party") && deliveryPriceValue > 0 && (
                          <div>Frais de livraison: {deliveryPriceValue.toLocaleString("fr-FR")} F CFA</div>
                        )}
                      </div>
                      <strong style={{ fontSize: "1.2rem" }}>
                        Total: {total.toLocaleString("fr-FR")} F CFA
                      </strong>
                    </div>
                  );
                })()}
              </div>

              <button
                type="submit"
                className="btn btn-primary btn-lg"
                disabled={ordering || orderSuccess}
                style={{ width: "100%" }}
                data-tour="product-order-submit"
              >
                {ordering ? "Traitement..." : "Commander"}
              </button>
            </form>
          </div>
        )}


        {!isAuthenticated && (
          <div className="card" style={{ textAlign: "center" }}>
            <p>Vous devez être connecté pour commander</p>
            <Link to="/login" className="btn btn-primary">
              Se connecter
            </Link>
          </div>
        )}
      </div>

      {/* Modal de paiement */}
      {createdOrder && (
        <PaymentModal
          isOpen={showPaymentModal}
          onClose={() => {
            setShowPaymentModal(false);
            // Rediriger vers les commandes après fermeture du modal
            navigate("/orders");
          }}
          order={createdOrder}
          onPaymentSuccess={() => {
            setShowPaymentModal(false);
            // Rediriger vers les commandes après paiement réussi
            navigate("/orders");
          }}
        />
      )}
    </div>
  );
};

export default ProductDetail;
